/**
 * useEmiratesIdScan — subscribes to the EmiratesId desk-scanner SignalR
 * hub for the patient edit screen.
 *
 * Hub: `/api/identity-server/NotificationsHub?username=<user email>`
 * Method: `EmiratesIdCardData`
 * Frame shape (locked from Flutter `create_patient_screen.dart:151`):
 *   - SignalR Invoke arguments[0] = `{ emiratesIdCardData: { idNumber, ... } }`
 *
 * Lifecycle mirrors `use-scheduling-realtime.ts`: connect on enabled,
 * stop on cleanup, StrictMode-safe via a `disposed` guard so the
 * cleanup-during-negotiation race doesn't throw.
 *
 * **What this hook does NOT do:**
 *   - Doesn't parse the payload — caller passes a raw `onScan` and uses
 *     `parseEmiratesIdScan` from `./emirates-id-parser` to normalize.
 *     Keeps the parser independently testable.
 *   - Doesn't trigger Save re-enable. React's `formState.isValid`
 *     recomputes after the consumer's `form.setValue` calls, so
 *     Flutter's 1 s `EnablePatientSaveEvent` debounce is implicit here.
 *   - Doesn't trigger the matching-records fetch. The form's existing
 *     `useMatchingPatients` subscription fires automatically when
 *     `identification.idNumber` changes via `setValue`.
 *
 * Flutter ref: `create_patient_screen.dart:138` (`initiateSignalR()`),
 *              `:226` (dispose).
 */

import { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { createHubConnection } from '@/shared/realtime/signalr-connection';
import { useAuthStore } from '@/auth/auth-store';
import type {
  EmiratesIdCardData,
  EmiratesIdCardDataMessage,
} from '@/shared/types/emirates-id';

const HUB_BASE_PATH = '/api/identity-server/NotificationsHub';

export interface UseEmiratesIdScanOptions {
  /** Toggle the connection — pass `false` on routes where the listener
   *  should be torn down (e.g. unmounted edit page). */
  enabled: boolean;
  /** Fires on every received `EmiratesIdCardData` frame with the inner
   *  payload (already unwrapped from `{ emiratesIdCardData: {...} }`).
   *  Caller is responsible for normalising + applying to form. */
  onScan: (data: EmiratesIdCardData) => void;
}

export function useEmiratesIdScan({ enabled, onScan }: UseEmiratesIdScanOptions): void {
  const userEmail = useAuthStore((s) => s.user?.email);

  useEffect(() => {
    if (!enabled || !userEmail) return;

    const url = `${HUB_BASE_PATH}?username=${encodeURIComponent(userEmail)}`;
    const getToken = () => useAuthStore.getState().getValidAccessToken();
    const hub = createHubConnection(url, getToken);

    hub.on('EmiratesIdCardData', (...args: unknown[]) => {
      const data = unwrapScanFrame(args);
      if (data) onScan(data);
    });

    // StrictMode dev double-invokes this effect. The flow:
    //   1. First mount: hub1 created, start() awaited inside the IIFE.
    //   2. Cleanup runs BEFORE start() resolves → sets `disposed=true`.
    //   3. Start finally resolves; the IIFE sees `disposed && state!==Disconnected`
    //      and stops hub1 itself. The connection is torn down even though
    //      cleanup couldn't stop it directly (state was `Connecting`).
    //   4. Second mount: hub2 created with its own `disposed=false`. Each
    //      hub owns its lifetime — they never share state.
    // Same pattern as scheduling realtime, validated in prod.
    let disposed = false;

    void (async () => {
      try {
        if (hub.state === HubConnectionState.Disconnected) {
          await hub.start();
        }
        if (disposed && hub.state !== HubConnectionState.Disconnected) {
          await hub.stop();
        }
      } catch {
        // withAutomaticReconnect handles real failures; StrictMode-induced
        // mid-negotiation aborts are expected and silenced by the connection's
        // quiet logger.
      }
    })();

    return () => {
      disposed = true;
      if (hub.state === HubConnectionState.Connected) {
        void hub.stop().catch(() => {});
      }
    };
    // onScan deliberately omitted — capturing it would resubscribe on
    // every render. Callers should keep the handler stable (useCallback)
    // OR accept that the closure sees the handler from connection-time.
    // The form-setValue use case is safe either way because RHF setValue
    // is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userEmail]);
}

/**
 * SignalR `connection.on(method, (...args) => {})` hands the handler a
 * variadic arg list — same convention as scheduling's
 * `firstAppointment` helper. The .NET server invokes
 * `Clients.User(...).SendAsync("EmiratesIdCardData", payload)`, so
 * `args[0]` is the `{ emiratesIdCardData: {...} }` wrapper.
 *
 * No defensive flat-payload branch: Flutter's parser asserts the wrapper
 * shape (`EmiratesIdCardDataModel.fromJson(message.first)` reads
 * `json["emiratesIdCardData"]`), so the contract is single-shape. If
 * the backend ever simplifies, the change is observable in a single
 * test — better than an untested branch silently masking a regression.
 */
function unwrapScanFrame(args: unknown[]): EmiratesIdCardData | null {
  const head = args[0];
  if (!head || typeof head !== 'object') return null;
  if (!('emiratesIdCardData' in head)) return null;
  return (head as EmiratesIdCardDataMessage).emiratesIdCardData ?? null;
}
